import { closeReconciliationURLs } from 'routes/close/closeURLs';

import { CommentNotificationDisplay } from 'components/comments/CommentNotificationDisplay';
import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { featureFlags } from 'utils/config/featureFlags';
import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';

import {
  ReconTaskCommentReplyAddedNotification,
  ReconTaskCommentReplyEditedNotification,
  ReconTaskCommentReplyNotification,
  ReconTaskCommentReplyTaggedNotification,
} from 'types/inbox-notifications/close/ReconciliationNotifications';
import { SegmentAtByDot } from 'types/utils';

const { FF__SHOW_NOTIFICATIONS_DEBUG_MODE: DEBUG_MODE } = featureFlags;

type CommentReplyLevelKeys = SegmentAtByDot<
  ReconTaskCommentReplyNotification['type'],
  5
>;

export function CommentReplyNotificationItem({
  type,
  item,
}: {
  type: string[];
  item: ReconTaskCommentReplyNotification;
}) {
  const [actionType, ..._restOfType] = type;

  switch (actionType as CommentReplyLevelKeys) {
    case 'added':
      return (
        <ReplyAddedNotificationItem
          item={item as ReconTaskCommentReplyAddedNotification}
        />
      );
    case 'edited':
      return (
        <ReplyEditedNotificationItem
          item={item as ReconTaskCommentReplyEditedNotification}
        />
      );
    case 'tagged':
      return (
        <ReplyTaggedNotificationItem
          item={item as ReconTaskCommentReplyTaggedNotification}
        />
      );
    default:
      return (
        <div>
          Unknown reconciliation comment reply notification type: {actionType}
        </div>
      );
  }
}

function ReplyAddedNotificationItem({
  item,
}: {
  item: ReconTaskCommentReplyAddedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply)
    return DEBUG_MODE ? (
      <div>
        [DEBUG] Reply not found ({item.comment.id}/{item.reply_id})
      </div>
    ) : null;

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} replied to a comment.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}

function ReplyEditedNotificationItem({
  item,
}: {
  item: ReconTaskCommentReplyEditedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply)
    return DEBUG_MODE ? (
      <div>
        [DEBUG] Reply not found ({item.comment.id}/{item.reply_id})
      </div>
    ) : null;

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} updated a reply.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}

function ReplyTaggedNotificationItem({
  item,
}: {
  item: ReconTaskCommentReplyTaggedNotification;
}) {
  const { usersMap } = useUsersData();
  const reply = item.comment.replies.find(
    (reply) => reply.id === item.reply_id
  );

  if (!reply)
    return DEBUG_MODE ? (
      <div>
        [DEBUG] Reply not found ({item.comment.id}/{item.reply_id})
      </div>
    ) : null;

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskSingleComment({
        taskId: item.task.core_snapshot.short_code,
        commentId: item.comment.id,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${getName(item.actor_id, usersMap)} tagged you in a reply.`}
      description={<CommentNotificationDisplay comment={reply} />}
    />
  );
}
